import { Fragment } from "react";
import type { FleetStats } from "../api/types";
import { VEHICLE_LIVE_STATE_META } from "../api/types";
import type { RouteOption } from "./liveFleet";

interface MapStatsOverlayProps {
  /** False until the live state is known — the roster, the first fleet
   *  snapshot, device sessions and trips have all arrived. Counters resolved
   *  before then are structurally wrong (every bus "No GPS" for want of
   *  positions), and render as "—" rather than a credible count. */
  statsKnown?: boolean;
  /** Computed once in Layout.tsx (the single source both TopBar and this
   *  overlay read from) so the two can never show different counts. */
  stats: FleetStats;
  /** Routes a bus is running a trip on — plus the chosen one after its last
   *  trip has ended, so a filter that now hides every bus stays visible and
   *  can be cleared. */
  routes: RouteOption[];
  routeFilter: RouteOption | null;
  onRouteFilter: (route: RouteOption | null) => void;
}

interface Counter {
  /** Also the cell's modifier class (mso-stat--duty, …). */
  id: string;
  label: string;
  value: string;
  /** Matches the markers the counter counts, from the same state table. */
  dot: string;
  /** A ring around the dot: LATE and EARLY count on-trip buses, drawn green
   *  on the map, so their dot is the on-trip green and the ring says which
   *  way off schedule. A bare amber dot was the No GPS marker's colour. */
  ring?: string;
  title: string;
  tone?: "warn" | "crit";
  pulse?: boolean;
}

export default function MapStatsOverlay({ stats, routes, routeFilter, onRouteFilter, statsKnown = true }: MapStatsOverlayProps) {
  const n = (v: number) => (statsKnown ? String(v) : "—");
  const meta = VEHICLE_LIVE_STATE_META;
  // Each bus is in exactly one of the counters after ON DUTY (FleetStats).
  const counters: Counter[] = [
    {
      id: "duty", label: "On duty", value: statsKnown ? `${stats.onDuty}/${stats.total}` : "—/—",
      dot: "var(--brand-primary)",
      title: "In service, of the active fleet: every bus reporting a GPS fix, plus any whose trip has gone quiet (LOST). Offline and no-GPS buses are not on duty",
    },
    { id: "on-time", label: "On time", value: n(stats.onTime), dot: meta.on_trip.color, title: "On a trip, from 1 minute early to 5 minutes late" },
    {
      id: "late", label: "Late", value: n(stats.late), dot: meta.on_trip.color, ring: "var(--sev-warn)",
      tone: stats.late > 0 ? "warn" : undefined,
      title: "On a trip, more than 5 minutes behind schedule",
    },
    // Like LOST, only while there is one: running ahead is off schedule too,
    // but calling it LATE said the opposite of what the bus was doing.
    ...(statsKnown && stats.early > 0
      ? [{
          id: "early", label: "Early", value: n(stats.early), dot: meta.on_trip.color, ring: "var(--sev-info)",
          title: "On a trip, more than 1 minute ahead of schedule",
        }]
      : []),
    // Only while it is happening: a permanent zero beside LATE is noise, and a
    // non-zero LOST is the one counter that must not be missed.
    ...(statsKnown && stats.lost > 0
      ? [{
          id: "lost", label: "Lost", value: n(stats.lost), dot: meta.lost_contact.color, tone: "crit" as const, pulse: true,
          title: "A trip is in progress but its bus has stopped reporting",
        }]
      : []),
    {
      id: "no-trip", label: "No trip", value: n(stats.noTrip), dot: meta.moving_no_trip.color,
      title: "Moving, GPS healthy, but no trip claimed — usually a tablet nobody signed in on",
    },
    {
      id: "idle", label: "Idle", value: n(stats.idle),
      // Idle is two states, unclaimed and standing by: the dot carries both.
      dot: `linear-gradient(90deg, ${meta.unclaimed.color} 50%, ${meta.standing_by.color} 50%)`,
      title: "Reporting, not moving, no trip — unclaimed or standing by",
    },
    { id: "no-gps", label: "No GPS", value: n(stats.noGps), dot: meta.no_gps.color, title: "Tablet online, but no GPS fix" },
    { id: "offline", label: "Offline", value: n(stats.offline), dot: meta.offline.color, title: "Tablet unreachable" },
  ];

  return (
    <div className="map-stats-overlay">
      <div className="mso-stats" role="group" aria-label="Fleet status">
        {counters.map((c, i) => (
          <Fragment key={c.id}>
            {i > 0 && <div className="mso-divider" />}
            <div className={`mso-stat mso-stat--${c.id}`} title={c.title}>
              <span className={"mso-value mono" + (c.tone ? ` mso-${c.tone}` : "")}>{c.value}</span>
              <span className="mso-label">
                <span
                  className={"mso-dot" + (c.pulse ? " pulse" : "")}
                  style={{ background: c.dot, boxShadow: c.ring ? `0 0 0 2px ${c.ring}` : undefined }}
                />
                {c.label}
              </span>
            </div>
          </Fragment>
        ))}
      </div>

      {/* Only routes a bus is actually on. With no trip running anywhere the
          row used to be a lone "All lines" pill that did nothing. */}
      {(routes.length > 0 || routeFilter) && (
        <div className="mso-routes" role="group" aria-label="Show one route">
          <button
            className={`mso-route-pill${routeFilter === null ? " on" : ""}`}
            onClick={() => onRouteFilter(null)}
            aria-pressed={routeFilter === null}
            type="button"
          >
            All lines
          </button>
          {routes.map((r) => {
            const on = routeFilter?.key === r.key;
            return (
              <button
                key={r.key}
                className={`mso-route-pill${on ? " on" : ""}`}
                onClick={() => onRouteFilter(on ? null : r)}
                aria-pressed={on}
                title={on ? "Showing only this route — click to show all" : `Show only buses on ${r.label}`}
                type="button"
              >
                <span className="mso-route-dot" style={{ background: r.color ?? "var(--text-4)" }} />
                {r.label}
                <span className="mso-route-count mono">{r.count}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
